import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EndemicityMap from "./EndemicityMap";
import Header from "../Components/Header";
import "../assets/css/HotspotMapping.css";
import { categoryColors } from "./EndemicityMap";
import {
  getHotSpotKebele,
  getHotSpotZone,
  getHotSpotWoreda,
  getHotSpotRegionalState,
} from "../Services/apiService";
const LEVELS = ["regional-state", "zone", "woreda", "kebele"];

export default function HotspotMapping() {
  // ?level=woreda&q=Name (links from Risk Prediction) opens that list, searched
  const [params] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(
    LEVELS.includes(params.get("level")) ? params.get("level") : "regional-state"
  );
  const [kebeles, setKebeles] = useState([]);
  const [zones, setZones] = useState([]);
  const [woredas, setWoredas] = useState([]);
  const [regionalStates, setRegionalState] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  useEffect(() => {
    const fetchKebeles = async () => {
      setLoading(true);
      try {
        const data = await getHotSpotKebele();
        setKebeles(data);
      } catch (error) {
        setError("Failed to load kebele data");
      } finally {
        setLoading(false);
      }
    };
    const fetchZones = async () => {
      setLoading(true);
      try {
        const data = await getHotSpotZone();
        setZones(data);
      } catch (error) {
        setError("Failed to load zone data");
      } finally {
        setLoading(false);
      }
    };
    const fetchWoredas = async () => {
      setLoading(true);
      try {
        const data = await getHotSpotWoreda();
        setWoredas(data);
      } catch (error) {
        setError("Failed to load zone data");
      } finally {
        setLoading(false);
      }
    };
    const fetchRegionalState = async () => {
      setLoading(true);
      try {
        const data = await getHotSpotRegionalState();
        setRegionalState(data);
      } catch (error) {
        setError("Failed to load regional state data");
      } finally {
        setLoading(false);
      }
    };
    fetchWoredas();
    fetchRegionalState();
    fetchZones();
    fetchKebeles();
  }, []);
  // console.log("kebeles", kebeles); //15670
  // console.log("zones", zones); //132
  // console.log("woredas", woredas); //867
  // console.log("regional states", regionalStates); //12;

  const filterItems = (items, searchKey) => {
  if (!items || !items.length) return [];
  const keyMap = {
    "Regional State": "regionalState",
    Zone: "zone",
    Woreda: "woreda",
    Kebele: "kebele",
  };
  const actualKey = keyMap[searchKey];
  return items.filter((item) => {
    const value = item[actualKey];
    if (!value) return false;
    return value.toLowerCase().includes(searchQuery.toLowerCase());
  });
};
  const getFilteredData = () => {
  switch (statusFilter) {
    case "regional-state":
      return filterItems(regionalStates, "Regional State");
    case "zone":
      return filterItems(zones, "Zone");
    case "woreda":
      return filterItems(woredas, "Woreda");
    case "kebele":
      return filterItems(kebeles, "Kebele");
    default:
      return [];
  }
};
  const filteredData = getFilteredData();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);
  const getTextColor = (bgColor) => {
    const r = parseInt(bgColor.slice(1, 3), 16);
    const g = parseInt(bgColor.slice(3, 5), 16);
    const b = parseInt(bgColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? "#1a202c" : "#ffffff";
  };
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("regional-state");
  };

  return (
    <div className="hotspot-mapping-container">
      <Header />
      <div className="hotspot-layout">
        <div className="map-container flex-item">
          <div className="map-card">
            <EndemicityMap />
          </div>
        </div>
        <div className="hotspot-panel flex-item">
          <div className="panel-card">
            <div className="panel-header">
              <h3 className="panel-title">Disease Hotspots</h3>
              <div className="panel-actions">
                <div className="location-count">{filteredData.length}</div>
                <button className="clear-btn" onClick={clearFilters}>
                  Clear
                </button>
              </div>
            </div>
            <div className="panel-search" role="search">
              <input
                type="text"
                placeholder="Search locations or region..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search hotspots"
              />
            </div>
            <div className="filter-chips" aria-label="Filters">
              <button
                className={`chip ${
                  statusFilter === "regional-state" ? "active" : ""
                }`}
                onClick={() => setStatusFilter("regional-state")}
              >
                Regional State
              </button>
              <button
                className={`chip ${statusFilter === "zone" ? "active" : ""}`}
                onClick={() => setStatusFilter("zone")}
              >
                Zone
              </button>
              <button
                className={`chip ${statusFilter === "woreda" ? "active" : ""}`}
                onClick={() => setStatusFilter("woreda")}
              >
                Woreda
              </button>
              <button
                className={`chip ${statusFilter === "kebele" ? "active" : ""}`}
                onClick={() => setStatusFilter("kebele")}
              >
                Kebele
              </button>
            </div>
            <div className="panel-list" role="list">
              {loading ? (
                <div className="table-spinner-container">
                  <div className="spinner-hotspot"></div>
                </div>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item) => {
                  const getTitle = () => {
                    switch (statusFilter) {
                      case "regional-state":
                        return item?.regionalState || "Unknown";
                      case "zone":
                        return item?.zone || "Unknown";
                      case "woreda":
                        return item?.woreda || "Unknown";
                      case "kebele":
                        return item?.kebele || "Unknown";
                      default:
                        return (
                          item.regionalState ||
                          item.zone ||
                          item.woreda ||
                          item.kebele ||
                          "Unknown"
                        );
                    }
                  };
                  const title = getTitle();
                  const getSubtitle = () => {
                    const limitText = (text) => {
                      if (!text) return text;
                      return text.length > 16
                        ? text.substring(0, 16) + "..."
                        : text;
                    };
                    switch (statusFilter) {
                      case "regional-state":
                        return (
                          <span className="hotspot-subtitle">
                            <strong>Capital:</strong>{" "}
                            {limitText(item.capitalCity) || "Unknown"}
                          </span>
                        );
                      case "zone":
                        return (
                          <span className="hotspot-subtitle">
                            <strong>Regional State:</strong>{" "}
                            {limitText(item.regionalState) || "Unknown"}
                          </span>
                        );
                      case "woreda":
                        return (
                          <span className="hotspot-subtitle">
                            <strong>Zone:</strong>{" "}
                            {limitText(item.zone) || "Unknown"}
                          </span>
                        );
                      case "kebele":
                        return (
                          <span className="hotspot-subtitle">
                            <strong>Woreda:</strong>{" "}
                            {limitText(item.woreda) || "Unknown"}
                          </span>
                        );
                      default:
                        return null;
                    }
                  };
                  const endemicity = item.endemicity || "No Data";
                  const avatarText =
                    typeof title === "string"
                      ? title
                          .split(" ")
                          .map((s) => s[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()
                      : "??";
                  return (
                    <div key={item._id} className="hotspot-row" role="listitem">
                      <div className="hotspot-avatar" aria-hidden>
                        {avatarText}
                      </div>
                      <div className="hotspot-info">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <div>
                            <div className="hotspot-name">{title}</div>
                            <div
                              className="hotspot-meta"
                              style={{ width: "100%" }}
                            >
                              {getSubtitle()}
                              {statusFilter === "woreda" && item.woreda && (
                                <Link
                                  className="hotspot-risk-link"
                                  to={`/risk-prediction?view=woredas&woreda=${encodeURIComponent(item.woreda)}`}
                                  title="Model prediction for this woreda (if there is one)"
                                >
                                  Risk prediction
                                </Link>
                              )}
                              {item.Zone &&
                                item.Regional_State &&
                                item.Zone !== item.Regional_State && (
                                  <>
                                    <span>•</span>
                                    <span>{item.Regional_State}</span>
                                  </>
                                )}
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                backgroundColor:
                                  categoryColors[endemicity] || "#e2e8f0",
                                color: getTextColor(
                                  categoryColors[endemicity] || "#e2e8f0"
                                ),
                              }}
                              className="hotspot-endemicity"
                            >
                              {endemicity
                                ? endemicity.replace(/\s*\([^)]*\)/g, "")
                                : ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-results">
                  <div>
                    <strong>
                      {searchQuery ? "No results found" : "No locations found"}
                    </strong>
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {searchQuery
                      ? "Try adjusting your search or clear filters."
                      : "No locations to display."}
                  </div>
                </div>
              )}
            </div>
            {!loading && filteredData.length > itemsPerPage && (
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}